import React, { useState, useContext, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import * as Sentry from "@sentry/react"
import {
  Box,
  TextField,
  Button,
  Link,
  Tooltip,
  Grid,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
  useTheme,
  Typography,
  Checkbox,
} from "@mui/material"
import LoginError from "../../../components/LoginError"
import { F_START_AGB, F_START_EMAIL_VALIDATION, F_START_FORGOT_PASSWORD, F_START_MINORINFO, F_TRADING, F_VERWALTUNG } from "../../../constants/routes"
import UserContext from "../../../context/UserContext"
import useMutation from "../../../hooks/useMutation"

const getCustomColorSx = (theme) => ({
  "& .MuiInputLabel-root.Mui-focused": {
    color: theme.palette.start.main,
  },
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": {
      borderColor: theme.palette.start.main,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.start.main,
    },
  },
})

const LoginForm = ({ email, setEmail, password, setPassword, switchPage, navigate, theme }) => (
  <>
    <Link
      variant="caption"
      href="#"
      sx={{
        color: "gray",
        textDecoration: "none",
      }}
      onClick={() => switchPage("Register")}>
      Noch kein Account?
    </Link>
    <TextField
      label="E-Mail / Nutzername"
      margin="normal"
      required
      fullWidth
      id="email"
      name="email"
      autoComplete="email"
      // autoFocus
      onChange={(e) => setEmail(e.target.value)}
      variant="outlined"
      sx={{
        backgroundColor: "white",
        height: "3.5rem",
        borderRadius: "0.3rem",
        ...getCustomColorSx(theme),
      }}
    />
    <Link
      variant="caption"
      onClick={() => navigate(F_START_FORGOT_PASSWORD)}
      sx={{
        color: "gray",
        textDecoration: "none",
      }}>
      Passwort vergessen?
    </Link>
    <TextField
      label="Passwort"
      margin="normal"
      required
      fullWidth
      name="password"
      type="password"
      id="password"
      autoComplete="current-password"
      onChange={(e) => setPassword(e.target.value)}
      sx={{ backgroundColor: "white", height: "3.5rem", borderRadius: "0.3rem", ...getCustomColorSx(theme) }}
    />
    <Button
      type="submit"
      fullWidth
      disabled={!(email.length > 0 && password.length > 0)}
      sx={{
        mt: 3,
        color: "#000",
        backgroundColor: "start.main",
        height: "3.5rem",

        "&:hover": {
          backgroundColor: "start.hover",
          color: "#000",
        },
      }}>
      Login
    </Button>
  </>
)

const RegisterForm = ({
  schoolcode,
  setSchoolcode,
  email,
  setEmail,
  password,
  setPassword,
  isMobile,
  theme,
  navigate,
  agbChecked,
  setAgbChecked,
  minorInfoChecked,
  setMinorInfoChecked,
}) => (
  <>
    {/* <Typography paragraph align="center" sx={{ cursor: "pointer" }} color="primary.main" onClick={() => setCurrentPage("Lizenzen und Module")}>
      Registrierung für Schulen nur über <u>Lizenzen und Module</u>
    </Typography> */}
    {/* <Typography paragraph variant="subtitle1" align="center">
      Registrierung für Schüler
    </Typography> */}
    <Tooltip title="Den Schulcode erhälst Du von Deiner Schule" arrow placement="bottom">
      <Typography
        variant="caption"
        sx={{
          color: "gray",
          display: "block",
        }}>
        {isMobile ? "Den Schulcode erhälst Du von Deiner Schule" : " Wo finde ich meinen Schulcode?"}
      </Typography>
    </Tooltip>
    <TextField
      label="Schulcode"
      // autoFocus
      margin="normal"
      required
      fullWidth
      id="schulcode"
      name="schulcode"
      onChange={(e) => setSchoolcode(e.target.value)}
      variant="outlined"
      sx={{ backgroundColor: "white", height: "3.5rem", borderRadius: "0.3rem", ...getCustomColorSx(theme) }}
    />
    <TextField
      margin="normal"
      label="Email"
      required
      fullWidth
      id="email"
      name="email"
      onChange={(e) => setEmail(e.target.value)}
      variant="outlined"
      sx={{ backgroundColor: "white", height: "3.5rem", borderRadius: "0.3rem", ...getCustomColorSx(theme) }}
    />
    <TextField
      margin="normal"
      label="Passwort"
      required
      fullWidth
      name="password"
      type="password"
      id="password"
      onChange={(e) => setPassword(e.target.value)}
      sx={{
        backgroundColor: "white",
        height: "3.5rem",
        borderRadius: "0.3rem",
        ...getCustomColorSx(theme),
      }}
    />

    <Box sx={{ display: "flex", alignItems: "flex-start", flexDirection: "row", mt: 1 }}>
      <Checkbox sx={{ pl: 0, pr: 0.5, pt: 0 }} value={agbChecked} onChange={() => setAgbChecked((v) => !v)} />
      <Typography variant="caption" sx={{ display: "block" }}>
        Ich habe die{" "}
        <Typography variant="caption" onClick={() => navigate(F_START_AGB)}>
          <u>
            <b>AGB</b>
          </u>
        </Typography>{" "}
        gelesen und akzeptiert
      </Typography>
    </Box>

    <Box sx={{ display: "flex", alignItems: "flex-start", flexDirection: "row", mt: 1 }}>
      <Checkbox sx={{ pl: 0, pr: 0.5, pt: 0 }} value={minorInfoChecked} onChange={() => setMinorInfoChecked((v) => !v)} />
      <Typography variant="caption" sx={{ display: "block" }}>
        Ich habe den{" "}
        <Typography variant="caption" onClick={() => navigate(F_START_MINORINFO)}>
          <u>
            <b>Hinweis</b>
          </u>
        </Typography>{" "}
        zur Registrierung für minderjährige Nutzer gelesen und akzeptiert
      </Typography>
    </Box>

    <Button
      type="submit"
      fullWidth
      disabled={!agbChecked || !minorInfoChecked || !(email.length > 0 && password.length > 0 && schoolcode.length > 0)}
      sx={{
        mt: 2,
        color: "#000",
        backgroundColor: "start.main",
        height: "3.5rem",

        "&:hover": {
          backgroundColor: "start.hover",
          color: "#000",
        },
      }}>
      Registrieren
    </Button>
  </>
)

const Login = ({ isMobile }) => {
  const navigate = useNavigate()
  const theme = useTheme()

  const { mutate: loginMutation, loading: loginLoading } = useMutation("/auth/login")
  const { mutate: registerMutation, loading: registerLoading } = useMutation("/auth/register")

  const { userData, setUserDataExternal } = useContext(UserContext)
  const [errorMsg, setErrorMsg] = useState(null)
  const [snackbarMsg, setSnackbarMsg] = useState(null)

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [schoolcode, setSchoolcode] = useState("")
  const [agbChecked, setAgbChecked] = useState(false)
  const [minorInfoChecked, setMinorInfoChecked] = useState(false)
  const [activePage, setActivePage] = useState("Login")

  const loading = loginLoading || registerLoading

  useEffect(() => {
    const isLoggedIn = userData?.token
    if (isLoggedIn) {
      if (userData?.user) {
        navigate(F_TRADING)
        return
      }
      if (userData?.school) {
        navigate(F_VERWALTUNG)
        return
      }
    }
  }, [navigate, userData])

  const switchPage = (page) => {
    setErrorMsg(null)
    setActivePage(page)
    setEmail("")
    setPassword("")
    setSchoolcode("")
  }

  const handleLogin = async (event) => {
    event.preventDefault()
    setErrorMsg(null)
    try {
      const res = await loginMutation({ username: email, password })

      if (res?.token) {
        setUserDataExternal(res)

        // user
        if (res?.user) {
          Sentry.setUser({
            id: res.user.id,
            userType: "user",
          })
          Sentry.setTag("user-bookay-id", res.user.id)
          Sentry.setTag("user-bookay-type", "user")
          navigate(F_TRADING)
          return
        }

        // school
        if (res?.school) {
          Sentry.setUser({
            id: res.school.id,
            userType: "school",
          })
          Sentry.setTag("user-bookay-id", res.school.id)
          Sentry.setTag("user-bookay-type", "school")
          navigate(F_VERWALTUNG)
          return
        }
      }
    } catch (e) {
      console.log(e.message)
      setErrorMsg(e.message)
    }
  }

  const handleRegister = async (event) => {
    event.preventDefault()
    setErrorMsg(null)

    try {
      const res = await registerMutation({ email, password, schoolcode })

      if (res?.email) {
        localStorage.setItem("validateEmail", JSON.stringify(res?.email))
        navigate(F_START_EMAIL_VALIDATION)
      }
    } catch (e) {
      console.log(e.message)
      setErrorMsg(e.message)
    }
  }

  return (
    <Grid container justifyContent="center">
      <Grid item xs={10} md={4}>
        <Box
          component="form"
          onSubmit={activePage === "Login" ? handleLogin : handleRegister}
          noValidate
          sx={{
            mx: "auto",
            mt: 3,
            mb: 1,
            borderWidth: "2px",
            borderStyle: "solid",
            borderColor: "start.main",
            borderRadius: "12px",
            p: 3,
            width: "100%",
          }}>
          <Box sx={{ mb: 2, display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 1 }}>
            <Button
              onClick={() => {
                switchPage("Register")
              }}
              variant="outlined"
              sx={{
                fontSize: "smaller",
                backgroundColor: activePage !== "Login" ? "start.main" : "white",
                color: activePage !== "Login" ? "#000" : "start.main",
                borderColor: "start.main",
                "&:hover": {
                  backgroundColor: "start.hover",
                  color: "#000",
                },
              }}>
              Registrieren
            </Button>
            <Button
              onClick={() => {
                switchPage("Login")
              }}
              variant="outlined"
              sx={{
                fontSize: "smaller",
                backgroundColor: activePage !== "Register" ? "start.main" : "white",
                color: activePage !== "Register" ? "#000" : "start.main",
                borderColor: "start.main",
                "&:hover": {
                  backgroundColor: "start.hover",
                  color: "#000",
                },
              }}>
              Login
            </Button>
          </Box>

          {activePage === "Login" && (
            <LoginForm
              theme={theme}
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              switchPage={switchPage}
              navigate={navigate}
            />
          )}

          {activePage === "Register" && (
            <RegisterForm
              theme={theme}
              schoolcode={schoolcode}
              setSchoolcode={setSchoolcode}
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              isMobile={isMobile}
              navigate={navigate}
              agbChecked={agbChecked}
              setAgbChecked={setAgbChecked}
              setMinorInfoChecked={setMinorInfoChecked}
              minorInfoChecked={minorInfoChecked}
            />
          )}

          <LoginError msg={errorMsg} />

          <Snackbar
            open={!!snackbarMsg}
            onClose={(event, reason) => {
              if (reason === "clickaway") return
              setSnackbarMsg(null)
            }}>
            <Alert onClose={() => setSnackbarMsg(null)} severity="success" sx={{ width: "100%" }}>
              {snackbarMsg}
            </Alert>
          </Snackbar>

          <Backdrop sx={(theme) => ({ backgroundColor: "rgba(0,0,0,0.1)", zIndex: theme.zIndex.drawer + 1 })} open={loading}>
            <CircularProgress size={60} thickness={5} sx={{ color: "start.hover" }} />
          </Backdrop>
        </Box>
      </Grid>
    </Grid>
  )
}

export default Login
